/**
 * Enriched summary statistics builder.
 *
 * Calculates comprehensive summary statistics from collected test data,
 * including API call metrics, assertion stats, and navigation stats.
 */

import type {
  Summary,
  ApiCallsByStatusRange,
  ApiResponseTimeStats,
  ApiCallRecord,
  ApiAssertion,
  NavigationAnnotation,
  TestStatus,
  ActionStep,
  VisualComparison,
} from '@testrelic/core';

/** Minimal test data shape needed for summary calculation. */
export interface SummaryTestInput {
  readonly status: TestStatus;
  readonly navigations: readonly NavigationAnnotation[];
  readonly apiCalls?: readonly ApiCallRecord[] | null;
  readonly apiAssertions: readonly ApiAssertion[] | null;
  readonly actions: readonly ActionStep[] | null;
  readonly visual?: readonly VisualComparison[] | null;
}

/**
 * Calculate the nearest-rank percentile from a sorted array.
 * Index = ceil(P/100 * N) - 1, clamped to [0, N-1].
 */
export function nearestRankPercentile(sorted: number[], percentile: number): number {
  const n = sorted.length;
  const index = Math.min(Math.ceil((percentile / 100) * n) - 1, n - 1);
  return sorted[Math.max(0, index)];
}

/** Strip query parameters and fragment from a URL for uniqueness comparison. */
export function stripUrlQuery(rawUrl: string): string {
  try {
    const parsed = new URL(rawUrl);
    return parsed.origin + parsed.pathname;
  } catch {
    return rawUrl;
  }
}

/** Categorize a response status code into a status range key. */
function getStatusRange(statusCode: number | null): keyof ApiCallsByStatusRange {
  if (statusCode === null || statusCode === undefined) return 'error';
  if (statusCode >= 200 && statusCode < 300) return '2xx';
  if (statusCode >= 300 && statusCode < 400) return '3xx';
  if (statusCode >= 400 && statusCode < 500) return '4xx';
  if (statusCode >= 500 && statusCode < 600) return '5xx';
  return 'error';
}

/**
 * Build enriched summary statistics from collected test data.
 *
 * @param tests - Array of collected test data
 * @param timelineLength - Number of steps in the unified timeline
 * @returns Enriched Summary object with all 17 fields
 */
export function buildEnrichedSummary(
  tests: readonly SummaryTestInput[],
  timelineLength: number,
): Summary {
  // Test status counts
  let passed = 0;
  let failed = 0;
  let flaky = 0;
  let skipped = 0;
  let timedout = 0;

  for (const test of tests) {
    switch (test.status) {
      case 'passed': passed++; break;
      case 'failed': failed++; break;
      case 'flaky': flaky++; break;
      case 'skipped': skipped++; break;
      case 'timedout': timedout++; break;
    }
  }

  // Flatten all API calls
  const allApiCalls: ApiCallRecord[] = [];
  for (const test of tests) {
    if (test.apiCalls) {
      allApiCalls.push(...test.apiCalls);
    }
  }

  // API call statistics
  const totalApiCalls = allApiCalls.length;
  const apiUrlSet = new Set<string>();
  const methodCounts: Record<string, number> = {};
  // The published shape is readonly; the accumulator must not be. Tallying
  // into a `Readonly` alias was five assignment errors, one per key.
  const statusRanges: Record<keyof ApiCallsByStatusRange, number> =
    { '2xx': 0, '3xx': 0, '4xx': 0, '5xx': 0, error: 0 };
  const responseTimes: number[] = [];

  for (const call of allApiCalls) {
    apiUrlSet.add(stripUrlQuery(call.url));
    methodCounts[call.method] = (methodCounts[call.method] ?? 0) + 1;
    statusRanges[getStatusRange(call.responseStatusCode)] += 1;
    responseTimes.push(call.responseTimeMs);
  }

  // Response time percentiles
  let apiResponseTime: ApiResponseTimeStats | null = null;
  if (responseTimes.length > 0) {
    const sorted = [...responseTimes].sort((a, b) => a - b);
    const sum = sorted.reduce((acc, v) => acc + v, 0);
    apiResponseTime = {
      avg: Math.round(sum / sorted.length),
      min: sorted[0],
      max: sorted[sorted.length - 1],
      p50: nearestRankPercentile(sorted, 50),
      p95: nearestRankPercentile(sorted, 95),
      p99: nearestRankPercentile(sorted, 99),
    };
  }

  // Assertion statistics
  let totalAssertions = 0;
  let passedAssertions = 0;
  let failedAssertions = 0;
  for (const test of tests) {
    if (test.apiAssertions) {
      for (const assertion of test.apiAssertions) {
        totalAssertions++;
        if (assertion.status === 'passed') passedAssertions++;
        else failedAssertions++;
      }
    }
  }

  // Navigation statistics
  let totalNavigations = 0;
  const navUrlSet = new Set<string>();
  for (const test of tests) {
    totalNavigations += test.navigations.length;
    for (const nav of test.navigations) {
      navUrlSet.add(nav.url);
    }
  }

  // Action step statistics
  let totalActionSteps = 0;
  const actionStepsByCategory: Record<string, number> = {};
  function countActions(steps: readonly ActionStep[]): void {
    for (const step of steps) {
      totalActionSteps++;
      actionStepsByCategory[step.category] = (actionStepsByCategory[step.category] ?? 0) + 1;
      if (step.children.length > 0) {
        countActions(step.children);
      }
    }
  }
  for (const test of tests) {
    if (test.actions) {
      countActions(test.actions);
    }
  }

  // Visual comparison counts. A `new` or `updated` snapshot is neither a
  // pass nor a failure — nothing was compared — so it is counted apart
  // rather than folded into either bucket.
  let visualChecks = 0;
  let visualFailures = 0;
  let visualNew = 0;
  let visualDomChanges = 0;
  for (const test of tests) {
    if (!test.visual) continue;
    for (const comparison of test.visual) {
      visualChecks++;
      if (comparison.status === 'failed') visualFailures++;
      else if (comparison.status === 'new' || comparison.status === 'updated') visualNew++;
      visualDomChanges += comparison.domChangeCount ?? 0;
    }
  }

  return {
    total: tests.length,
    passed,
    failed,
    flaky,
    skipped,
    timedout,
    totalApiCalls,
    uniqueApiUrls: apiUrlSet.size,
    apiCallsByMethod: methodCounts,
    apiCallsByStatusRange: statusRanges,
    apiResponseTime,
    totalAssertions,
    passedAssertions,
    failedAssertions,
    totalNavigations,
    uniqueNavigationUrls: navUrlSet.size,
    totalTimelineSteps: timelineLength,
    totalActionSteps,
    actionStepsByCategory,
    visualChecks,
    visualFailures,
    visualNew,
    visualDomChanges,
  };
}
